'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useWebstoreSession } from '../auth/webstore-session-provider';
import { useAddAddress } from '@/api/use-add-new-address';
import StoreLocation from '@/app/[locale]/[city]/components/location/store-location';
import { getAuthToken } from '@/lib/auth/get-token';
import { generatePlaceId } from '@/lib/fast-location-utils';
import { useCartStore } from '@/store/cart/cart-store-provider';

type OrderStoreLocationProps = {
  onClose: () => void;
  isMobile?: boolean;
};

/**
 * Order-flow wrapper around the shared `StoreLocation` picker (the same one used
 * by the header). Keeps the address-selection UI/behaviour identical to the
 * header while wiring it to the order context: cart store as source of truth +
 * persisting the chosen address to the server when authenticated.
 */
export default function OrderStoreLocation({
  onClose,
  isMobile,
}: OrderStoreLocationProps) {
  const queryClient = useQueryClient();
  const { storeData } = useWebstoreSession();

  const address = useCartStore((state) => state.address) ?? null;
  const setCartAddress = useCartStore((state) => state.setAddress);

  const systemToken = getAuthToken();

  // `mutate` is referentially stable across renders; destructuring it (instead
  // of depending on the whole mutation object) keeps `handleAddressChange`
  // stable so StoreLocation's memoized suggestion list isn't re-rendered on
  // every parent render.
  const { mutate: persistAddress, isPending: isPersistingAddress } =
    useAddAddress(systemToken, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['user-locations'] });
      },
      onError: () => {
        toast.error('Có lỗi khi lưu địa chỉ, vui lòng thử lại sau');
      },
    });

  const handleAddressChange = useCallback(
    (newAddress: Address) => {
      // Cart store is the source of truth for the order trigger. StoreLocation
      // already syncs the global ai-food address + city id internally.
      setCartAddress(newAddress);

      // Persist to server when authenticated (parity with the header flow).
      if (
        systemToken &&
        typeof newAddress.lat === 'number' &&
        typeof newAddress.lng === 'number' &&
        newAddress.address &&
        !isPersistingAddress
      ) {
        persistAddress({
          address: newAddress.address,
          place_id:
            newAddress.place_id ??
            generatePlaceId(newAddress.lat, newAddress.lng),
          lat: newAddress.lat,
          lng: newAddress.lng,
        });
      }
    },
    [setCartAddress, systemToken, persistAddress, isPersistingAddress]
  );

  const session = storeData
    ? {
        store_name: storeData.name,
        store_image_url: storeData.image_url,
      }
    : null;

  return (
    <StoreLocation
      address={address}
      onAddressChange={handleAddressChange}
      onClose={onClose}
      clearInputOnly
      session={session}
      isMobile={isMobile}
    />
  );
}
